import React, { useMemo } from 'react';
import ApprovalRequestCard from '../approvals/ApprovalRequestCard';
import ApprovalGroupCard from '../approvals/ApprovalGroupCard';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The employee's own expenses, and any batches of them, as cards.
 *
 * The cards are the ones the approvals queue uses, so an expense looks the same
 * to the person who filed it and the person deciding it.
 *
 * A card opens the expense and does nothing else. Deleting one lives inside that
 * modal, next to the expense it destroys, rather than a click away on a grid of
 * cards where the wrong one is easy to hit.
 *
 * Also used for the contents of one batch inside the group modal.
 */
export default function ExpenseCardList({
  expenses,
  onView,
  showApprovalDetails = false,
  // Batches render as their own card style in the same grid, so an employee
  // sees groups and loose expenses together.
  groups = [],
  onOpenGroup,
}) {
  const auth = useAuth();
  const data = useMemo(() => expenses || [], [expenses]);
  const groupData = useMemo(() => groups || [], [groups]);
  const currentEmployeeId = auth.employee?.id;

  if (data.length === 0 && groupData.length === 0) {
    return <div className="text-muted">No expenses found.</div>;
  }

  return (
    <div className="approval-card-grid">
      {groupData.map((group) => (
        <ApprovalGroupCard
          key={group.id}
          group={group}
          onReview={onView}
          onOpen={onOpenGroup}
        />
      ))}

      {data.map((expense) => {
        // This list holds both the employee's own expenses and any they filed
        // for someone else, so each card has to say which it is — otherwise a
        // colleague's expense is indistinguishable from your own.
        const isForSomeoneElse = Boolean(
          currentEmployeeId
          && expense.employee_id
          && String(expense.employee_id) !== String(currentEmployeeId)
        );
        const filedByOther = Boolean(
          currentEmployeeId
          && expense.submitted_by
          && String(expense.submitted_by) !== String(currentEmployeeId)
        );
        const ownerName = `${expense.first_name || ''} ${expense.last_name || ''}`.trim()
          || expense.email
          || 'another employee';
        const submitterName = `${expense.submitter_first_name || ''} ${expense.submitter_last_name || ''}`.trim()
          || expense.submitter_email
          || 'someone else';

        const attribution = isForSomeoneElse
          ? `For ${ownerName}`
          : (filedByOther ? `Filed by ${submitterName}` : '');

        return (
          <ApprovalRequestCard
            key={expense.id}
            kind="expense"
            record={expense}
            onReview={onView}
            primaryLabel="View"
            showStatus
            showApprovalDetails={showApprovalDetails}
            attribution={attribution}
          />
        );
      })}
    </div>
  );
}
